/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, getter-return, no-prototype-builtins, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import moment from 'moment';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  findBy,
  mapBy,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';

const today = moment().format('YYYY-MM-DD');

export default class YearLevelCountsController extends Controller {
  @action
  setDate(value) {
    this.date = value;
  }

  queryParams = ['date', 'funding'];

  // Services
  @service
  fieldOptions;

  @service
  session;

  @service
  store;

  enrolmentSort = Object.freeze(['exitDate:desc', 'entryDate:desc']);

  @sort('model.enrolments', 'enrolmentSort')
  sortedEnrolments;

  @tracked date = today;
  today = today;
  @tracked funding = false;

  get reportTitle() {
    let funding = this.funding ? 'Funding ' : '';
    return `${funding}Year Level Counts as at ${this.date}`;
  }

  @alias('session.school')
  school;

  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('fieldOptions.yearLevelOptions.[]')
  get yearLevelMap() {
    if (!isEmpty(this.fieldOptions.yearLevelOptions)) {
      return this.fieldOptions.yearLevelOptions[0].options;
    }
  }

  @computed('school.{yearLevels.[],schoolType}')
  get yearLevels() {
    let yearLevels = this.school.yearLevels;
    if (parseInt(this.school.schoolType, 10) === 21) {
      // We add years 7 & 8 for Contributing Primary Schools for this report
      yearLevels.push('7', '8');
    }
    return yearLevels;
  }

  @tracked
  sortProp = 'name';
  @tracked
  sortDir = 'asc';

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @computed('model.genderField.options.[]')
  get genderValues() {
    let options = this.get('model.genderField.options');
    if (isEmpty(options)) {
      return ['M', 'F'];
    }
    return mapBy(options, 'value');
  }

  @alias('genderValues.length')
  colspan;

  @computed(
    'date',
    'model.homerooms.[]',
    'sortedEnrolments.[]',
    'yearLevelMap.[]',
    'jurisdiction',
  )
  get tableData() {
    let isInternationalJurisdiction = this.jurisdiction !== Jurisdiction.NZ_MOE;
    let offset = moment(this.date).year() - moment().year();
    let excludeTypes = ['FF', 'NF', 'EM'];
    let enrolments = uniqBy(this.sortedEnrolments, 'personId');

    let data = this.model.homerooms.reduce(
      (roomsAcc, room) => {
        roomsAcc[room.id] = room.currentLearners.reduce((acc, learner) => {
          if (isEmpty(learner) || isEmpty(learner.person)) {
            // Learner not found - skip.
            return acc;
          }
          if (isEmpty(findBy(enrolments, 'personId', learner.person.id))) {
            // Learner not enrolled on selected date - skip.
            return acc;
          }
          if (this.funding) {
            // Funding Year Level Counts should exclude non-funded students.
            let type = learner.person.get('fields.nzMOE:type.value') ?? '';
            if (excludeTypes.includes(type)) {
              roomsAcc.personIds.push(learner.person.id); // Mark as processed so we don't put them in as "left".
              return acc;
            }
          }

          let gender = learner.person.gender;

          let yearLevel;
          if (this.funding) {
            yearLevel = learner.person.calculateFundingYearLevel(this.date);
          } else {
            yearLevel = learner.person.numericCurrentYearLevel;
            // Offset current year level if the requested date isn't in the current calendar year.
            // This is very naive - it just adjusts it by however many calendar years' difference there is.
            yearLevel += offset;
          }
          if (isInternationalJurisdiction && !isEmpty(this.yearLevelMap)) {
            // Convert back to the label, using the offset year level
            let yearLevelOption = this.yearLevelMap.find(
              (item) => item.valueInt === yearLevel,
            );
            if (!isEmpty(yearLevelOption)) {
              yearLevel = yearLevelOption.label;
            }
          }
          if (acc.hasOwnProperty(yearLevel)) {
            if (acc[yearLevel].hasOwnProperty(gender)) {
              acc[yearLevel][gender]++;
            } else {
              acc[yearLevel][gender] = 1;
            }
          } else {
            acc[yearLevel] = {
              [gender]: 1,
            };
          }

          // Mark this learner as processed.
          roomsAcc.personIds.push(learner.person.id);

          return acc;
        }, {});

        return roomsAcc;
      },
      { personIds: [] },
    );

    if (data.personIds.length < enrolments.length) {
      // We have some learners who we haven't counted yet - mark them as "Left".
      data['left'] = {};
      let acc = data['left'];
      enrolments.forEach((enrolment) => {
        if (data.personIds.includes(enrolment.personId)) {
          return;
        }
        let person = this.store.peekRecord('person', enrolment.personId);
        let gender = person.gender;

        let yearLevel;
        if (this.funding) {
          // Funding Year Level Counts should exclude non-funded students.
          let type = person.get('fields.nzMOE:type.value') ?? '';
          if (excludeTypes.includes(type)) {
            return;
          }
          yearLevel = person.calculateFundingYearLevel(this.date);
        } else {
          yearLevel = person.numericCurrentYearLevel;
          // Offset current year level if the requested date isn't in the current calendar year.
          // This is very naive - it just adjusts it by however many calendar years' difference there is.
          yearLevel += offset;
          if (isInternationalJurisdiction && !isEmpty(this.yearLevelMap)) {
            // Convert back to the label, using the offset year level
            let yearLevelOption = this.yearLevelMap.find(
              (item) => item.valueInt === yearLevel,
            );
            if (!isEmpty(yearLevelOption)) {
              yearLevel = yearLevelOption.label;
            }
          }
        }

        if (acc.hasOwnProperty(yearLevel)) {
          if (acc[yearLevel].hasOwnProperty(gender)) {
            acc[yearLevel][gender]++;
          } else {
            acc[yearLevel][gender] = 1;
          }
        } else {
          acc[yearLevel] = {
            [gender]: 1,
          };
        }

        // Mark this learner as processed.
        data.personIds.push(person.id);
      });
    }

    // Remove personIds array which we used to track who had been processed,
    // as it would mess with subsequent calculations.
    delete data['personIds'];

    return data;
  }

  @computed('tableData')
  get yearGenderTotals() {
    return Object.values(this.tableData).reduce((acc, years) => {
      Object.entries(years).forEach((entry) => {
        let year = entry[0];
        let genderCounts = entry[1];

        if (!acc.hasOwnProperty(year)) {
          acc[year] = {};
        }

        Object.entries(genderCounts).forEach((entry) => {
          let gender = entry[0];
          let genderCount = entry[1];

          if (acc[year].hasOwnProperty(gender)) {
            acc[year][gender] += genderCount;
          } else {
            acc[year][gender] = genderCount;
          }
        });
      });

      return acc;
    }, {});
  }

  @computed('yearGenderTotals')
  get yearTotals() {
    return Object.entries(this.yearGenderTotals).reduce((years, entry) => {
      let year = entry[0];
      let genders = entry[1];

      years[year] = Object.values(genders).reduce(
        (acc, count) => acc + count,
        0,
      );

      return years;
    }, {});
  }

  @computed('yearLevels.[]', 'tableData')
  get roomGenderTotals() {
    return Object.entries(this.tableData).reduce((rooms, entry) => {
      let room = entry[0];
      let years = entry[1];

      rooms[room] = Object.entries(years).reduce((totals, entry) => {
        if (!this.yearLevels.includes(entry[0])) {
          // If a year we are processing is not in school.yearLevels
          return totals;
        }
        let genders = entry[1];

        Object.entries(genders).forEach((entry) => {
          let gender = entry[0];
          let count = entry[1];

          if (totals.hasOwnProperty(gender)) {
            totals[gender] += count;
          } else {
            totals[gender] = count;
          }
        });

        return totals;
      }, {});

      return rooms;
    }, {});
  }

  @computed('roomGenderTotals')
  get roomTotals() {
    return Object.entries(this.roomGenderTotals).reduce((rooms, entry) => {
      let room = entry[0];
      let genders = entry[1];

      rooms[room] = Object.values(genders).reduce(
        (acc, count) => acc + count,
        0,
      );

      return rooms;
    }, {});
  }

  @computed('roomGenderTotals')
  get totals() {
    let genders = Object.values(this.roomGenderTotals).reduce(
      (genders, room) => {
        Object.entries(room).forEach((entry) => {
          let gender = entry[0];
          let count = entry[1];

          if (genders.hasOwnProperty(gender)) {
            genders[gender] += count;
          } else {
            genders[gender] = count;
          }
        });

        return genders;
      },
      {},
    );

    return {
      genders,
      total: Object.values(genders).reduce((acc, val) => acc + val, 0),
    };
  }
}
